import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTicket, useTicketAttachments, useUploadAttachment } from '@/lib/adminApi';
import type { TicketSummary } from '@ventin/shared';
import { PriorityBadge, CategoryBadge } from '@/components/tickets/ticketMeta';
import { CommentThread } from '@/components/tickets/CommentThread';
import { ScreenshotCaptureFlow } from '@/components/screenshot/ScreenshotCaptureFlow';
import { AttachmentGallery } from '@/components/screenshot/gallery/AttachmentGallery';
import type { ScreenshotResult } from '@/components/screenshot/types';

/**
 * PortalTicketDetailPage — /portal/tickets/:id (TKT-07 read-only).
 *
 * Shows ticket detail for a CLIENT user. All interactive controls
 * (billable toggle, usesIncrements checkbox, status select) are
 * intentionally absent — those are SUPPORT/ADMIN-only operations.
 *
 * 404 handling: API returns 404 for cross-company lookups (Plan 03-03
 * service-layer enforcement). We surface a generic friendly message
 * so no cross-company info leaks.
 */
export default function PortalTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useTicket(id ?? '');
  const { data: attachmentsData } = useTicketAttachments(id ?? '');
  const attachments = attachmentsData?.attachments ?? [];
  const uploadMut = useUploadAttachment();

  async function handleAttachScreenshot(result: ScreenshotResult) {
    if (!id) return;
    try {
      await uploadMut.mutateAsync({ id, result });
      toast.success('Screenshot attached');
    } catch {
      toast.error("Couldn't attach screenshot — try again");
    }
  }

  if (isLoading) {
    return (
      <div>
        <p className="text-sm text-muted-foreground">Loading ticket…</p>
      </div>
    );
  }

  if (isError || !data?.ticket) {
    return (
      <div>
        <Link
          to="/portal/tickets"
          className="text-sm text-muted-foreground hover:text-foreground mb-4 inline-block"
        >
          ← Back to My Tickets
        </Link>
        <div className="rounded-md border bg-background p-6 text-center">
          <p className="text-sm font-medium">Ticket not found or not visible to you</p>
          <p className="text-xs text-muted-foreground mt-1">
            The ticket may not exist or may belong to a different account.
          </p>
        </div>
      </div>
    );
  }

  const ticket = data.ticket;

  return (
    <div className="max-w-2xl">
      {/* Back link */}
      <Link
        to="/portal/tickets"
        className="text-sm text-muted-foreground hover:text-foreground mb-4 inline-block"
      >
        ← Back to My Tickets
      </Link>

      {/* Header */}
      <div className="mb-6 mt-2">
        <h2 className="text-2xl font-semibold leading-[1.3]">{ticket.title}</h2>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <TicketStatusChip status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
          <CategoryBadge category={ticket.category} />
          <span className="text-sm text-muted-foreground">{ticket.source}</span>
        </div>
      </div>

      {/* Description */}
      <section className="rounded-md border bg-background p-5 mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Description
        </h3>
        <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
      </section>

      {/* Metadata */}
      <section className="rounded-md border bg-background p-5 space-y-3 text-sm mb-4">
        <MetaRow label="Created" value={new Date(ticket.createdAt).toLocaleDateString()} />
        {ticket.resolvedAt && (
          <MetaRow label="Resolved" value={new Date(ticket.resolvedAt).toLocaleDateString()} />
        )}
      </section>

      {/* Screenshots: attach + gallery (no capture metadata for clients). */}
      <section className="rounded-md border bg-background p-5 space-y-4 mb-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Screenshots
          </h3>
          <ScreenshotCaptureFlow
            onComplete={handleAttachScreenshot}
            disabled={uploadMut.isPending}
          />
        </div>
        {uploadMut.isPending && (
          <p className="text-xs text-muted-foreground">Uploading screenshot…</p>
        )}
        <AttachmentGallery attachments={attachments} />
      </section>

      {/* Public comment thread — internal notes are never returned to CLIENT. */}
      {id && <CommentThread ticketId={id} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium w-24 shrink-0">{label}:</span>
      <span className="text-muted-foreground">{value}</span>
    </div>
  );
}

const STATUS_STYLES: Record<TicketSummary['status'], { bg: string; dot: string; label: string }> = {
  OPEN: { bg: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', label: 'Open' },
  IN_PROGRESS: { bg: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500', label: 'In Progress' },
  WAITING_CLIENT: { bg: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400', label: 'Waiting' },
  RESOLVED: { bg: 'bg-green-50 text-green-700', dot: 'bg-green-500', label: 'Resolved' },
  CLOSED: { bg: 'bg-[#F1F1F3] text-[#4B4B52]', dot: 'bg-[#8A8A94]', label: 'Closed' },
};

function TicketStatusChip({ status }: { status: TicketSummary['status'] }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        s.bg,
      )}
    >
      <span aria-hidden="true" className={cn('inline-block h-[6px] w-[6px] rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}
